import { cn } from '@/lib/utils'

interface ResizeHandleProps {
  /** Mouse-down handler that initiates the drag. */
  onMouseDown: (e: React.MouseEvent) => void
  /** Optional double-click handler (e.g., reset to default width). */
  onDoubleClick?: () => void
  /** Whether the user is actively dragging — controls the active brand-tinted state. */
  isResizing?: boolean
  /** Accessible label. Defaults to "Resize panel". */
  'aria-label'?: string
  className?: string
  /** Keyboard resizing. When set, the handle is focusable (tab stop). */
  onKeyDown?: (e: React.KeyboardEvent) => void
  /** Current / bounds, announced to screen readers for a focusable handle. */
  valueNow?: number
  valueMin?: number
  valueMax?: number
}

/**
 * Hairline resize handle shared by Library + editor pages.
 *
 * Visual pattern: always-visible 1px hairline (`before:bg-border`) that
 * thickens and goes brand-tinted on hover, and stays brand-tinted while
 * the user is dragging.
 */
export function ResizeHandle({
  onMouseDown,
  onDoubleClick,
  isResizing = false,
  'aria-label': ariaLabel = 'Resize panel',
  className,
  onKeyDown,
  valueNow,
  valueMin,
  valueMax,
}: ResizeHandleProps) {
  return (
    <div
      role="separator"
      aria-label={ariaLabel}
      aria-orientation="vertical"
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
      onKeyDown={onKeyDown}
      tabIndex={onKeyDown ? 0 : undefined}
      aria-valuenow={valueNow}
      aria-valuemin={valueMin}
      aria-valuemax={valueMax}
      className={cn(
        onKeyDown && 'focus-visible:outline-none focus-visible:before:!bg-[hsl(var(--brand))] focus-visible:before:!w-0.5',
        'relative w-1 cursor-col-resize shrink-0',
        'before:content-[""] before:absolute before:inset-y-0 before:left-1/2 before:w-px before:-translate-x-1/2',
        'before:bg-border before:transition-colors',
        'hover:before:bg-primary/40 hover:before:w-0.5',
        isResizing && 'before:!bg-primary/50 before:!w-0.5',
        className
      )}
    />
  )
}
